import type { TicketAttachmentDto } from "@/api/tickets";
import type { MessageValues, TranslationKey } from "@/locales/i18n";

/**
 * 附件的统一展示模型。编辑器里待发送的本地文件与时间线上已发出的服务端附件，
 * 在界面上长得一模一样，只是取内容的方式不同——用一个模型描述两者，
 * 预览、图标、体积这些渲染逻辑就不用写两份。
 */
export type TicketAttachmentView = {
  /** 列表 key：本地文件按「名字+大小+修改时间」，服务端附件按附件 id。 */
  key: string;
  name: string;
  size: number;
  contentType: string;
  /** 待发送的本地文件；已发出的附件为 null。 */
  file: File | null;
  /** 已落到服务端的附件坐标；本地草稿为 null。 */
  remote: { attachmentId: string; ticketId: string } | null;
};

/**
 * 允许的扩展名 → 展示用类型名。与后端 `TicketAttachmentOptions.AllowedFileTypes`
 * 保持同一份清单：前端拦一道只是为了别让用户白等一次上传，真正判定在服务端。
 */
const acceptedFileTypeLabelKeys: Record<string, TranslationKey> = {
  png: "tickets.attachment.type.image",
  jpg: "tickets.attachment.type.image",
  jpeg: "tickets.attachment.type.image",
  gif: "tickets.attachment.type.image",
  webp: "tickets.attachment.type.image",
  bmp: "tickets.attachment.type.image",
  pdf: "tickets.attachment.type.pdf",
  txt: "tickets.attachment.type.text",
  csv: "tickets.attachment.type.spreadsheet",
  log: "tickets.attachment.type.text",
  json: "tickets.attachment.type.text",
  xml: "tickets.attachment.type.text",
  md: "tickets.attachment.type.text",
  zip: "tickets.attachment.type.archive",
  doc: "tickets.attachment.type.document",
  docx: "tickets.attachment.type.document",
  xls: "tickets.attachment.type.spreadsheet",
  xlsx: "tickets.attachment.type.spreadsheet",
  ppt: "tickets.attachment.type.presentation",
  pptx: "tickets.attachment.type.presentation",
  mp4: "tickets.attachment.type.video",
  mp3: "tickets.attachment.type.audio",
  wav: "tickets.attachment.type.audio",
  m4a: "tickets.attachment.type.audio",
};

export const acceptedFileExtensions = Object.keys(acceptedFileTypeLabelKeys);

/** `<input type="file" accept>` 用的扩展名串。 */
export const fileInputAccept = acceptedFileExtensions.map((extension) => `.${extension}`).join(",");

/** 与后端 `TicketAttachments:MaxFileSize` / `MaxAttachmentsPerMessage` 的默认值一致。 */
export const maxAttachmentSize = 20 * 1024 * 1024;
export const maxAttachmentCount = 10;

const previewableExtensions = new Set(["png", "jpg", "jpeg", "gif", "webp", "bmp", "txt", "log", "csv", "json", "xml"]);

export function fileExtension(fileName: string): string {
  return fileName.split(".").pop()?.toLocaleLowerCase() ?? "";
}

export function isAcceptedFileName(fileName: string): boolean {
  return fileExtension(fileName) in acceptedFileTypeLabelKeys;
}

export function formatFileSize(bytes: number, intlLocale: string): string {
  if (bytes < 1024) return `${new Intl.NumberFormat(intlLocale).format(bytes)} B`;
  if (bytes < 1024 * 1024) return `${new Intl.NumberFormat(intlLocale).format(Math.ceil(bytes / 1024))} KB`;
  return `${new Intl.NumberFormat(intlLocale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(bytes / (1024 * 1024))} MB`;
}

export function isImageAttachment(attachment: TicketAttachmentView): boolean {
  return attachment.contentType.startsWith("image/");
}

/** 能不能在对话框里就地看：图片直接渲染，文本类读前若干 KB。其余只提供下载。 */
export function isPreviewableAttachment(attachment: TicketAttachmentView): boolean {
  return isImageAttachment(attachment) || attachment.contentType.startsWith("text/") || previewableExtensions.has(fileExtension(attachment.name));
}

export function attachmentTypeLabel(attachment: TicketAttachmentView, t: (key: TranslationKey, values?: MessageValues) => string): string {
  const extension = fileExtension(attachment.name);
  const labelKey = acceptedFileTypeLabelKeys[extension];
  if (labelKey) return t(labelKey);
  return extension.toLocaleUpperCase() || t("tickets.attachment.type.file");
}

const materialAttachmentIcons: Record<string, string> = {
  png: "fluent-color:image-24",
  jpg: "fluent-color:image-24",
  jpeg: "fluent-color:image-24",
  gif: "fluent-color:image-24",
  webp: "fluent-color:image-24",
  bmp: "fluent-color:image-24",
  pdf: "material-icon-theme:pdf",
  txt: "flat-color-icons:document",
  log: "vscode-icons:file-type-log",
  json: "vscode-icons:file-type-json",
  md: "vscode-icons:file-type-markdown",
  xml: "vscode-icons:file-type-xml",
  doc: "vscode-icons:file-type-word",
  docx: "vscode-icons:file-type-word",
  csv: "vscode-icons:file-type-excel",
  xls: "vscode-icons:file-type-excel",
  xlsx: "vscode-icons:file-type-excel",
  ppt: "vscode-icons:file-type-powerpoint",
  pptx: "vscode-icons:file-type-powerpoint",
  mp4: "vscode-icons:file-type-video",
  mp3: "material-icon-theme:audio",
  wav: "material-icon-theme:audio",
  m4a: "material-icon-theme:audio",
  zip: "material-icon-theme:zip",
  rar: "vscode-icons:file-type-zip",
};

export function attachmentIcon(attachment: TicketAttachmentView): string {
  return materialAttachmentIcons[fileExtension(attachment.name)] ?? "vscode-icons:default-file";
}

export function localAttachmentView(file: File): TicketAttachmentView {
  return {
    key: `${file.name}:${file.size}:${file.lastModified}`,
    name: file.name,
    // 本地只有浏览器猜的类型，服务端会按扩展名重新定；用于预览判断足够了。
    contentType: file.type,
    size: file.size,
    file,
    remote: null,
  };
}

export function remoteAttachmentView(attachment: TicketAttachmentDto): TicketAttachmentView {
  return {
    key: attachment.id,
    name: attachment.fileName,
    contentType: attachment.contentType,
    size: attachment.size,
    file: null,
    remote: { ticketId: attachment.ticketId, attachmentId: attachment.id },
  };
}

/** 把 Blob 存成文件。附件端点要带 Authorization，拿不到直链，只能先取回字节再交给浏览器。 */
export function saveBlobAs(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}
